export const PARTY = {
  D: {
    name: 'Democrat',
    plural: 'Democrats',
    // "Dem." in running text; "DEM" (no period) where text is set in all caps.
    short: 'Dem.',
    caps: 'DEM',
    fill: '#008AEF',
    // Darker than the fill so blue text meets 4.5:1 contrast on white.
    ink: '#0067B1',
    tint: '#84BBFA',
  },
  R: {
    name: 'Republican',
    plural: 'Republicans',
    short: 'GOP',
    caps: 'GOP',
    fill: '#CC0000',
    ink: '#CC0000',
    tint: '#ED9A9A',
  },
};

export const GRAY = '#C4C4C4';
// Votes are in but the top two are tied: darker than the no-results gray.
export const TIE_FILL = '#6B6B6B';

export function isTie(seat) {
  return !seat.shown && !seat.runoff && seat.candidates.some(candidate => candidate.votes > 0);
}

// Independents and third parties ('O'), keyed by the feed's party color.
const OTHER = {
  G: {
    name: 'Green', short: 'Green', fill: '#6BBD11', tint: '#C4E5A0',
  },
  I: {
    name: 'Independent', short: 'Ind.', fill: '#7A20A1', tint: '#D3A7E6',
  },
  L: {
    name: 'Libertarian', short: 'Lib.', fill: '#FF8E02', tint: '#FFD29A',
  },
};
const THIRD_PARTY = {
  name: 'Third-party', short: 'Other', fill: '#7A20A1', tint: '#D3A7E6',
};

export function partyStyle(seat, party) {
  if (PARTY[party]) return PARTY[party];
  if (party === 'O') {
    if (OTHER[seat.otherColor]) return OTHER[seat.otherColor];
    return seat.otherName ? { ...THIRD_PARTY, name: seat.otherName } : THIRD_PARTY;
  }
  return null;
}

// Undecided runoffs are hatched gray.
export const RUNOFF_FILL = 'repeating-linear-gradient(-45deg, #8A8A8A 0 1.5px, #FFFFFF 1.5px 3px)';

// "Dec. 12 runoff"
export function runoffWords(seat) {
  return seat.runoffDate ? `${seat.runoffDate} runoff` : 'runoff';
}

const AT_LARGE = new Set(['AK', 'DE', 'ND', 'SD', 'VT', 'WY']);

function ordinal(value) {
  const tens = value % 100;
  if (tens >= 11 && tens <= 13) return `${value}th`;
  return `${value}${{ 1: 'st', 2: 'nd', 3: 'rd' }[value % 10] || 'th'}`;
}

// "CA-1" reads as "California’s 1st District".
export function districtName(seat) {
  const state = seat.stateName || seat.state;
  if (!seat.district || AT_LARGE.has(seat.state)) return `${state}’s At-Large District`;
  return `${state}’s ${ordinal(seat.district)} District`;
}

export function districtCode(seat) {
  if (!seat.district || AT_LARGE.has(seat.state)) return `${seat.state}-AL`;
  return `${seat.state}-${String(seat.district).padStart(2, '0')}`;
}
const GOLD = '#E0A526';


function plural(count, word) {
  return `${count} ${word}${count === 1 ? '' : 's'}`;
}

function heldTarget(seat) {
  return seat.targeted && seat.called && seat.winner !== seat.seeker;
}

function category(seat) {
  if (seat.targeted) return 'tgt';
  if (seat.newMap) return 'new';
  return 'no';
}

// Seats with no votes yet sit with their 2024 holder.
function barSide(seat) {
  const party = seat.shown || seat.prevHolder;
  return party === 'D' || party === 'R' ? party : null;
}

function status(seat) {
  if (!seat.shown) return 'none';
  return seat.called ? 'won' : 'lead';
}

// Each side runs outer → inner; Republicans are mirrored onto the right.
// Within "New map", targeted seats sit between the won and leading seats, so
// solid seats stay together, leads stay together and the targeted cluster stays whole.
// Desktop keeps the targeted seats next to the 218 line, in the middle of the bar.
const SEAT_ORDER = [
  ['no', 'won'], ['no', 'lead'], ['no', 'none'],
  ['new', 'won'], ['new', 'lead'], ['new', 'none'],
  ['tgt', 'won'], ['tgt', 'lead'], ['tgt', 'none'],
];

// Mobile blocks wrap in columns of 8, so the targeted seats sit between the
// won and leading seats to keep the light leading squares grouped together.
const MOBILE_SEAT_ORDER = [
  ['no', 'won'], ['no', 'lead'], ['no', 'none'],
  ['new', 'won'], ['tgt', 'won'], ['tgt', 'lead'], ['new', 'lead'],
  ['tgt', 'none'], ['new', 'none'],
];

const OTHER_COLUMNS = 20;

// Fixed functions, so each layout's position lookup is built only once.
const mobileDemCoord = index => mobileCoord(index, true);
const mobileRepCoord = index => mobileCoord(index, false);
const otherCoord = index => ({ col: index % OTHER_COLUMNS, row: Math.floor(index / OTHER_COLUMNS) });

export function orderSeats(seats, order = SEAT_ORDER) {
  const side = (party) => {
    const list = [];
    order.forEach(([map, state]) => {
      seats.forEach((seat) => {
        if (barSide(seat) === party && category(seat) === map && status(seat) === state) list.push(seat);
      });
    });
    return list;
  };
  const other = seats.filter(seat => !barSide(seat));
  return side('D').concat(other, side('R').reverse());
}

export const DESKTOP_ROWS = 7;
export const DESKTOP_COLS = Math.ceil(435 / DESKTOP_ROWS);

function desktopCoord(index) {
  return {
    col: Math.floor(index / DESKTOP_ROWS),
    row: index % DESKTOP_ROWS,
  };
}

function mobileCoord(index, flip) {
  const mod = (index + 7) % 8;
  return {
    col: Math.floor((index + 7) / 8),
    row: flip ? 7 - mod : mod,
  };
}

// Rectangles covering the empty cells between a block's last seat and the
// 218 line, in mobileCoord order.
function mobileTrack(count, flip, cell) {
  const col = Math.floor((count + 6) / 8);
  const mod = (count + 6) % 8;
  const rects = [];
  if (col >= 28) return rects;
  if (mod < 7) {
    const rows = 7 - mod;
    rects.push({
      left: col * cell, top: (flip ? 0 : mod + 1) * cell, width: cell, height: rows * cell,
    });
  }
  if (col < 27) {
    rects.push({
      left: (col + 1) * cell, top: 0, width: (27 - col) * cell, height: 8 * cell,
    });
  }
  return rects;
}

const VERTICAL_COLUMNS = 18;
const VERTICAL_LABEL_GUTTER = 0;
const VERTICAL_BRACKET_GUTTER = 92;

function verticalCoord(index) {
  return {
    col: index % VERTICAL_COLUMNS,
    row: Math.floor(index / VERTICAL_COLUMNS),
  };
}

export function verticalCellSize(width) {
  const room = width - VERTICAL_LABEL_GUTTER - VERTICAL_BRACKET_GUTTER;
  return Math.max(5, Math.min(18, Math.floor((room / VERTICAL_COLUMNS) * 2) / 2));
}

function verticalLine(cell) {
  const row = Math.floor(218 / VERTICAL_COLUMNS);
  const col = 218 % VERTICAL_COLUMNS;
  const right = (VERTICAL_COLUMNS * cell) + 14;
  if (!col) return `M-6 ${row * cell} H${right}`;
  return `M-6 ${(row + 1) * cell} H${col * cell} V${row * cell} H${right}`;
}

function verticalBrackets(order, mode, cell) {
  const span = VERTICAL_COLUMNS;
  return bracketRuns(order).map((run, index, runs) => {
    const top = ((run.start / span) * cell) + (index ? 2 : 0);
    const bottom = (((run.end + 1) / span) * cell) - (index < runs.length - 1 ? 2 : 0);
    const height = Math.max(0, bottom - top);
    return {
      label: run.label,
      top,
      height,
      showLabel: height >= 14,
    };
  });
}

// Each layout's grid position → seat index, built once per layout function
// rather than scanning every position for every neighbor lookup.
const positions = new WeakMap();

function indexAt(coord, col, row) {
  let lookup = positions.get(coord);
  if (!lookup) {
    lookup = new Map();
    for (let i = 0; i < 500; i += 1) {
      const point = coord(i);
      const key = `${point.col},${point.row}`;
      if (!lookup.has(key)) lookup.set(key, i);
    }
    positions.set(coord, lookup);
  }
  const index = lookup.get(`${col},${row}`);
  return index === undefined ? -1 : index;
}

function neighborIndex(index, dc, dr, coord) {
  const current = coord(index);
  const col = current.col + dc;
  const row = current.row + dr;
  let lastRow = 7;
  if (coord === desktopCoord) lastRow = DESKTOP_ROWS - 1;
  else if (coord === verticalCoord) lastRow = Infinity;
  if (row < 0 || row > lastRow || col < 0) return -1;
  return indexAt(coord, col, row);
}

// Matches the 1px padding on individual squares, so a merged block's edge
// lines up with the edges of the squares beside it.
const MERGE_GAP = 1;

function mergedPadding(index, order, coord) {
  const seat = order[index];
  if (seat.newMap) return [1, 1, 1, 1];
  const gap = (dc, dr) => {
    const next = neighborIndex(index, dc, dr, coord);
    if (next < 0 || next >= order.length) return MERGE_GAP;
    return order[next].newMap ? MERGE_GAP : 0;
  };
  return [gap(0, -1), gap(1, 0), gap(0, 1), gap(-1, 0)];
}

// Where a merged block steps around a separate square, the cell tucked into
// that concave corner has no padding on either side, so its corner pokes into
// the gap. Clip that corner back by the gap width.
function mergedClip(index, order, coord) {
  const seat = order[index];
  if (seat.newMap) return 'none';
  const kind = (dc, dr) => {
    const next = neighborIndex(index, dc, dr, coord);
    if (next < 0 || next >= order.length) return 'empty';
    return order[next].newMap ? 'new' : 'merged';
  };
  const notch = (dc, dr) => kind(dc, dr) === 'new'
    && kind(dc, 0) === 'merged'
    && kind(0, dr) === 'merged';
  // Unclipped edges sit 1px outside the cell so the clip itself doesn't
  // antialias a seam against the neighbor at fractional cell sizes.
  const g = `${MERGE_GAP}px`;
  const far = `calc(100% - ${g})`;
  const lo = '-1px';
  const hi = 'calc(100% + 1px)';
  const corners = [
    notch(-1, -1) ? [`${lo} ${g}`, `${g} ${g}`, `${g} ${lo}`] : [`${lo} ${lo}`],
    notch(1, -1) ? [`${far} ${lo}`, `${far} ${g}`, `${hi} ${g}`] : [`${hi} ${lo}`],
    notch(1, 1) ? [`${hi} ${far}`, `${far} ${far}`, `${far} ${hi}`] : [`${hi} ${hi}`],
    notch(-1, 1) ? [`${g} ${hi}`, `${g} ${far}`, `${lo} ${far}`] : [`${lo} ${hi}`],
  ];
  if (corners.every(points => points.length === 1)) return 'none';
  return `polygon(${corners.flat().join(', ')})`;
}

// Targeted seats keep their gold outline before any results, so the bar
// shows where the fight is from the start.
function haloed(seat) {
  return category(seat) === 'tgt';
}

function goldShadow(index, order, coord, stroke = 1.5) {
  const parts = [`inset 0 0 0 ${stroke}px ${GOLD}`, `inset 0 0 0 ${stroke + 1}px #FFFFFF`];
  const targeted = (dc, dr) => {
    const next = neighborIndex(index, dc, dr, coord);
    return next >= 0 && next < order.length && haloed(order[next]);
  };
  const edges = {};
  [[0, -1, 0, 1, 't'], [0, 1, 0, -1, 'b'], [-1, 0, 1, 0, 'l'], [1, 0, -1, 0, 'r']].forEach((edge) => {
    if (!targeted(edge[0], edge[1])) {
      edges[edge[4]] = true;
      parts.push(`${-edge[2] * stroke}px ${-edge[3] * stroke}px 0 0 ${GOLD}`);
    }
  });
  [['t', 'l', -1, -1], ['t', 'r', 1, -1], ['b', 'l', -1, 1], ['b', 'r', 1, 1]].forEach((corner) => {
    if (edges[corner[0]] && edges[corner[1]]) {
      parts.push(`${corner[2] * stroke}px ${corner[3] * stroke}px 0 0 ${GOLD}`);
    }
  });
  return parts.join(', ');
}

function seatTitle(seat, mode) {
  if (mode === 'none') return '';
  const parts = [districtName(seat)];
  const party = partyStyle(seat, seat.shown);
  if (seat.runoff && !seat.called) parts.push(`headed to ${runoffWords(seat)}`);
  else if (!party) parts.push(seat.candidates.some(candidate => candidate.votes > 0) ? 'tied' : 'no votes yet');
  else if (seat.runoff) parts.push(`${party.name} seat`, `${party.name}s in ${runoffWords(seat)}`);
  else {
    parts.push(party.name);
    parts.push(seat.called ? 'won' : 'leading');
  }
  if (seat.targeted) parts.push('targeted');
  if (!seat.newMap) parts.push('no new map');
  return parts.join(' · ');
}

function seatFill(seat, mode, showFlipped) {
  if (mode === 'none') return GRAY;
  if (seat.runoff && !seat.called) return RUNOFF_FILL;
  const party = partyStyle(seat, seat.shown);
  if (!party) return isTie(seat) ? TIE_FILL : GRAY;
  const color = seat.isLeading ? party.tint : party.fill;
  if (showFlipped && seat.flipped) {
    return `radial-gradient(circle, #FFFFFF 0 1.1px, rgba(255,255,255,0) 1.6px), ${color}`;
  }
  return color;
}

function cellsFor(order, mode, showFlipped, coord, stroke = 1.5) {
  return order.map((seat, index) => {
    const point = coord(index);
    const halo = haloed(seat);
    const padding = halo ? [0, 0, 0, 0] : mergedPadding(index, order, coord);
    return {
      id: seat.id,
      col: point.col + 1,
      row: point.row + 1,
      fill: seatFill(seat, mode, showFlipped),
      shadow: halo ? goldShadow(index, order, coord, stroke) : 'none',
      pad: padding.map(value => `${value}px`).join(' '),
      clip: mergedClip(index, order, coord),
      radius: seat.newMap || halo ? '1px' : '0',
      z: halo ? 2 : 0,
      title: seatTitle(seat, mode),
    };
  });
}

function bracketRuns(order) {
  const runs = [];
  order.forEach((seat, index) => {
    const key = `${seat.newMap ? 'new' : 'no'}|${barSide(seat)}`;
    const last = runs[runs.length - 1];
    if (last && last.key === key) last.end = index;
    else {
      runs.push({
        key,
        label: seat.newMap ? 'New map' : 'No new map',
        start: index,
        end: index,
      });
    }
  });
  return runs;
}

// Stepped line between seat 218 and 219.
function desktopLine(cell) {
  const col = Math.floor(218 / DESKTOP_ROWS);
  const rem = 218 % DESKTOP_ROWS;
  const bottom = (DESKTOP_ROWS * cell) + 12;
  if (!rem) return `M${col * cell} 0 V${bottom}`;
  return `M${(col + 1) * cell} 0 V${(rem * cell) + 6} L${col * cell} ${((rem + 1) * cell) + 6} V${bottom}`;
}

export function desktopCellSize(width) {
  return Math.max(3, Math.min(18, Math.floor((width / DESKTOP_COLS) * 2) / 2));
}

// Leaves room for the 218 line's white halo past the last column.
const MOBILE_LABEL_OVERHANG = 3;

export function mobileCellSize(width, columns) {
  const room = width - MOBILE_LABEL_OVERHANG;
  return Math.max(5, Math.min(16, Math.floor(room / columns)));
}

// "No new map" brackets stay hidden on a side until both of that side's
// labels have room.
function untilBothLabels(items) {
  const sides = new Map();
  items.forEach((item) => {
    (item.sides || [item.side]).forEach((side) => {
      if (!sides.has(side)) sides.set(side, []);
      sides.get(side).push(item);
    });
  });
  const hidden = new Set();
  sides.forEach((group, side) => {
    const ready = ['new', 'no'].every(key => group.some(item => item.map === key && item.showLabel));
    if (!ready) hidden.add(side);
  });
  return items.filter(item => item.map !== 'no' || !hidden.has(item.side));
}

function desktopBrackets(order, mode, cell) {
  // Runs that share a column split it down the middle.
  const boundary = (run) => {
    const next = run.end + 1;
    return next % DESKTOP_ROWS === 0
      ? (next / DESKTOP_ROWS) * cell
      : (Math.floor(run.end / DESKTOP_ROWS) + 0.5) * cell;
  };
  const gap = 3;
  // Both parties' new-map seats meet in the middle, so they share one bracket.
  const merged = bracketRuns(order).reduce((list, run) => {
    const [map, side] = run.key.split('|');
    const last = list[list.length - 1];
    if (last && last.map === map) {
      last.end = run.end;
      last.sides.push(side);
    } else list.push({ ...run, map, sides: [side] });
    return list;
  }, []);
  return untilBothLabels(merged.map((run, index, runs) => {
    const left = index ? boundary(runs[index - 1]) + gap : 0;
    const right = index < runs.length - 1
      ? boundary(run) - gap
      : (Math.floor(run.end / DESKTOP_ROWS) + 1) * cell;
    const width = Math.max(0, right - left);
    return {
      label: run.label,
      map: run.map,
      side: run.sides[0],
      sides: run.sides,
      left,
      width,
      showLabel: width >= (run.label.length * 6.2) + 8,
    };
  }));
}

function mobileBrackets(order, cell) {
  const runs = [];
  order.forEach((seat, index) => {
    const key = seat.newMap ? 'new' : 'no';
    const last = runs[runs.length - 1];
    if (last && last.key === key) last.end = index;
    else {
      runs.push({
        key, label: seat.newMap ? 'New map' : 'No new map', start: index, end: index,
      });
    }
  });
  const line = 28 * cell;
  return untilBothLabels(runs.map((run, index, all) => {
    const pos = seatIndex => ((seatIndex + 7) / 8) * cell;
    const left = pos(run.start) + (index ? 2 : 0);
    const isLast = index === all.length - 1;
    const right = Math.min(pos(run.end + 1) - (isLast ? 0 : 2), line - 5);
    const width = Math.max(0, right - left);
    const needed = (run.label.length * 5.8) + 8;
    const fits = width >= needed;
    // A narrow last bracket can spill its label rightward into the empty
    // space before the 218 line.
    const spills = !fits && isLast && (line - 5 - left) >= needed;
    return {
      label: run.label,
      map: run.key,
      side: 'block',
      left,
      width,
      showLabel: fits || spills,
      align: spills ? 'start' : 'center',
      mid: left + (width / 2),
    };
  }));
}

function countParty(seats, field, party) {
  return seats.filter(seat => seat[field] === party).length;
}

// A 2024 presidential margin (Harris minus Trump, in points) as "Trump +25".
// Whole points, except a margin under 1 keeps a decimal so it doesn't read as 0.
export function marginText(margin) {
  if (margin == null) return { text: '—', ink: '#605D5D' };
  const size = Math.abs(margin);
  if (size < 0.05) return { text: 'Even', ink: '#605D5D' };
  const shown = size < 0.95 ? size.toFixed(1) : String(Math.round(size));
  return margin > 0
    ? { text: `Harris +${shown}`, ink: PARTY.D.ink }
    : { text: `Trump +${shown}`, ink: PARTY.R.ink };
}

function netChange(seats) {
  const called = seats.filter(seat => seat.called && seat.winner);
  const net = countParty(called, 'winner', 'R') - countParty(called, 'prevHolder', 'R');
  return {
    net,
    pending: seats.filter(seat => !seat.called).length,
    called: called.length,
  };
}

function targetShadow(stroke) {
  return `inset 0 0 0 1px #FFFFFF, 0 0 0 ${stroke}px ${GOLD}`;
}

export function stateSquares(seats, field) {
  const tagged = seats.map((seat) => {
    const after = partyStyle(seat, seat.shown) ? seat.shown : 'P';
    return {
      ...seat,
      bucket: field === 'after' ? after : seat.prevHolder,
      leading: field === 'after' && after !== 'P' && !seat.called,
    };
  });
  const group = (party, test) => tagged.filter(seat => seat.bucket === party && test(seat));
  const won = seat => !seat.leading;
  return [
    ...group('D', seat => won(seat) && !seat.targeted && !seat.flipped),
    ...group('D', seat => won(seat) && !seat.targeted && seat.flipped),
    ...group('D', seat => won(seat) && seat.targeted),
    ...group('D', seat => seat.leading && !seat.targeted),
    ...group('D', seat => seat.leading && seat.targeted),
    ...group('O', () => true),
    ...group('P', seat => seat.targeted),
    ...group('P', seat => !seat.targeted),
    ...group('R', seat => seat.leading && seat.targeted),
    ...group('R', seat => seat.leading && !seat.targeted),
    ...group('R', seat => won(seat) && seat.targeted),
    ...group('R', seat => won(seat) && !seat.targeted && seat.flipped),
    ...group('R', seat => won(seat) && !seat.targeted && !seat.flipped),
  ].map((seat) => {
    const tint = seat.leading;
    const party = partyStyle(seat, seat.bucket);
    const runoff = field === 'after' && seat.runoff;
    const tied = field === 'after' && isTie(seat);
    let color = GRAY;
    if (runoff && !party) color = RUNOFF_FILL;
    else if (party) color = tint ? party.tint : party.fill;
    else if (tied) color = TIE_FILL;
    const dotted = field === 'after' && seat.flipped && seat.bucket !== 'P';
    let result = 'No results yet';
    if (field === 'before') result = party ? `Won by ${party.short} in 2024` : 'No 2024 result';
    else if (runoff) result = `${party ? `${party.short} seat, ` : 'Headed to '}${runoffWords(seat)}`;
    else if (party) result = `${party.short} ${tint ? 'leading' : 'won'}`;
    else if (tied) result = 'Tied';
    return {
      id: seat.id,
      title: [districtName(seat), result, seat.targeted && 'targeted', dotted && 'flipped'].filter(Boolean).join(' · '),
      fill: dotted ? `radial-gradient(circle, #FFFFFF 0 1.0px, ${color} 1.4px)` : color,
      shadow: seat.targeted ? targetShadow(1.5) : 'none',
    };
  });
}

function aimFor(seats, party) {
  const list = seats.filter(seat => seat.targeted && seat.seeker === party);
  const got = list.filter(seat => seat.called && seat.winner === party);
  const pending = list.filter(seat => !seat.called);
  const held = list.filter(seat => seat.called && seat.winner && seat.winner !== party);
  const outcome = got.concat(held).concat(pending);
  return {
    party,
    aim: list.length,
    got: got.length,
    pending: pending.length,
    states: [...new Set(list.map(seat => seat.state))].length,
    previous: list,
    outcome,
  };
}

export function buildModel(seats, editorial, options) {
  const { mode } = options;
  // Only the bar on screen gets its seats built; with no layout given, all are built.
  const builds = kind => !options.layout || options.layout === kind;
  const anyFlipped = mode !== 'none' && seats.some(seat => seat.flipped);
  const showFlipped = options.showFlipped && anyFlipped;
  const ordered = orderSeats(seats);
  const desktopCoordFor = desktopCoord;
  const won = {
    D: countParty(seats, 'winner', 'D'),
    R: countParty(seats, 'winner', 'R'),
  };
  const leading = {
    D: seats.filter(seat => seat.isLeading && seat.leader === 'D').length,
    R: seats.filter(seat => seat.isLeading && seat.leader === 'R').length,
  };
  const runoffs = {
    open: seats.filter(seat => seat.runoff && !seat.called),
    decided: seats.filter(seat => seat.runoff && seat.called),
  };
  const aims = {
    D: aimFor(seats, 'D'),
    R: aimFor(seats, 'R'),
  };
  const cell = desktopCellSize(options.width || 700);
  // On mobile a block that crosses the 218 line reads as a majority, so each
  // block holds only seats its party has won or leads.
  const mobileOrdered = orderSeats(seats, MOBILE_SEAT_ORDER);
  const democrats = mobileOrdered.filter(seat => seat.shown === 'D');
  const republicans = mobileOrdered.filter(seat => seat.shown === 'R').reverse();
  // Independents and third parties get their own strip between the two blocks.
  const others = seats.filter(seat => seat.shown === 'O');
  const mobileOthers = others.filter(seat => seat.called).concat(others.filter(seat => !seat.called));
  const maxCount = Math.max(democrats.length, republicans.length);
  const mobileColumns = 28 + Math.max(0, Math.ceil((maxCount - 217) / 8));
  const mobileCell = mobileCellSize(options.width || 320, mobileColumns);

  const baseline = { D: 0, R: 0 };
  seats.forEach((seat) => {
    if (seat.prevHolder === 'D' || seat.prevHolder === 'R') baseline[seat.prevHolder] += 1;
  });

  const states = editorial.states.map((state) => {
    const rows = seats
      .filter(seat => seat.state === state.code)
      .sort((a, b) => a.district - b.district);
    const change = netChange(rows);
    const targeted = rows.filter(seat => seat.targeted);
    const got = targeted.filter(seat => seat.called && seat.winner === state.seeker).length;
    const seekerName = PARTY[state.seeker].plural;
    const opponents = PARTY[state.seeker === 'D' ? 'R' : 'D'].plural;
    // Net change counts called races only; leads show in the 2026 tally instead.
    // Gains are per party, so a seat lost to an independent isn't read as the other party's gain.
    const decided = rows.filter(seat => seat.called && seat.winner);
    const gain = party => countParty(decided, 'winner', party) - countParty(decided, 'prevHolder', party);
    const gains = { D: gain('D'), R: gain('R') };
    const net = gains.R - gains.D;
    let tone = 'tie';
    if (!decided.length) tone = 'wait';
    else if (net > 0) tone = 'R';
    else if (net < 0) tone = 'D';

    let chip = rows.some(seat => seat.shown || seat.runoff) ? 'No races called' : 'No results';
    if (decided.length) {
      const signed = value => (value > 0 ? `+${value}` : `\u2212${-value}`);
      if (gains.D !== -gains.R) {
        // A third party took a seat, so the two parties' changes don't mirror each other.
        // Both are listed, even at zero, so the chip reads clearly (e.g. "Dem. −1, GOP +0").
        chip = ['D', 'R'].map(party => `${PARTY[party].short}\u00a0${gains[party] ? signed(gains[party]) : '+0'}`).join(', ');
      } else if (gains.R > 0) chip = `${PARTY.R.short}\u00a0+${gains.R}`;
      else if (gains.D > 0) chip = `${PARTY.D.short}\u00a0+${gains.D}`;
      else chip = 'No\u00a0change';
      if (change.pending) chip += ' so far';
    }

    // "have flipped … so far" while races are open; "flipped" once all are called.
    const resultNote = change.pending
      ? `${seekerName} have flipped ${plural(got, 'targeted seat')} so far.`
      : `${seekerName} flipped ${plural(got, 'targeted seat')}.`;

    // Once every race is called, explain how the targeted flips add up to the net change.
    let netNote = '';
    if (!change.pending && rows.length) {
      const party = state.seeker;
      const otherGains = rows.filter(seat => !seat.targeted && seat.winner === party
        && seat.prevHolder !== party).length;
      const losses = rows.filter(seat => seat.prevHolder === party && seat.winner
        && seat.winner !== party).length;
      const overall = got + otherGains - losses;
      let outcome = 'broke even overall';
      if (overall > 0) outcome = `gained ${plural(overall, 'seat')} overall`;
      else if (overall < 0) outcome = `lost ${plural(-overall, 'seat')} overall`;
      if (otherGains && losses) {
        netNote = `${seekerName} also flipped ${plural(otherGains, 'other seat')} and lost ${plural(losses, 'seat')} in the state, so they ${outcome}.`;
      } else if (losses) {
        netNote = `${seekerName} lost ${plural(losses, 'other seat')} in the state, so they ${outcome}.`;
      } else if (otherGains) {
        netNote = `${seekerName} also flipped ${plural(otherGains, 'other seat')} in the state, so they ${outcome}.`;
      } else if (got) {
        netNote = `No other seats changed parties, so ${seekerName} ${outcome}.`;
      } else {
        netNote = 'No seats changed parties.';
      }
    }

    return {
      ...state,
      seats: rows,
      change,
      tone,
      chip,
      got,
      targeted,
      resultNote,
      netNote,
      aimNote: state.drawnBy === 'court'
        ? `The court-ordered map made ${plural(targeted.length, 'seat')} ${opponents} won in 2024 a ${state.seeker === 'D' ? 'Democratic' : 'Republican'} pickup opportunity.`
        : `With the new map, ${seekerName} targeted ${plural(targeted.length, 'seat')} ${opponents} won in 2024.`,
      beforeCounts: { D: countParty(rows, 'prevHolder', 'D'), R: countParty(rows, 'prevHolder', 'R') },
      afterCounts: {
        D: rows.filter(seat => seat.called && seat.winner === 'D').length,
        R: rows.filter(seat => seat.called && seat.winner === 'R').length,
        O: rows.filter(seat => seat.called && seat.winner === 'O').length,
        leading: {
          D: rows.filter(seat => seat.isLeading && seat.leader === 'D').length,
          R: rows.filter(seat => seat.isLeading && seat.leader === 'R').length,
          O: rows.filter(seat => seat.isLeading && seat.leader === 'O').length,
        },
      },
      otherFlips: rows.filter(seat => !seat.targeted && seat.flipped),
    };
  })
    // Most targeted seats first, so the mobile preview leads with the biggest efforts.
    .sort((a, b) => b.targeted.length - a.targeted.length || a.name.localeCompare(b.name));

  return {
    mode,
    updated: options.updated ? new Date(options.updated) : null,
    // The national feed's share of the expected vote counted; test stages pass
    // an average of their mock seats instead.
    pctReporting: mode === 'none' || options.pctReporting == null ? null : options.pctReporting,
    anyFlipped,
    showFlipped,
    held: {
      D: seats.filter(seat => heldTarget(seat) && seat.winner === 'D').length,
      R: seats.filter(seat => heldTarget(seat) && seat.winner === 'R').length,
    },
    won,
    leading,
    runoffs,
    others,
    aims,
    control: { D: won.D >= 218, R: won.R >= 218 },
    baseline,
    ordered,
    desktop: {
      cell,
      cells: builds('desktop') ? cellsFor(ordered, mode, showFlipped, desktopCoordFor) : [],
      brackets: desktopBrackets(ordered, mode, cell),
      width: DESKTOP_COLS * cell,
      height: (DESKTOP_ROWS * cell) + 12,
      line: desktopLine(cell),
    },
    mobile: {
      cell: mobileCell,
      columns: mobileColumns,
      width: mobileColumns * mobileCell,
      height: (16 * mobileCell) + 30,
      lineX: 28 * mobileCell,
      democrats: builds('mobile') ? cellsFor(democrats, mode, showFlipped, mobileDemCoord, 1) : [],
      republicans: builds('mobile') ? cellsFor(republicans, mode, showFlipped, mobileRepCoord, 1) : [],
      // Wraps at 20 squares, leaving room for the count beside the strip.
      others: builds('mobile') ? cellsFor(mobileOthers, mode, showFlipped, otherCoord, 1) : [],
      otherColumns: Math.max(1, Math.min(OTHER_COLUMNS, mobileOthers.length)),
      otherRows: Math.max(1, Math.ceil(mobileOthers.length / OTHER_COLUMNS)),
      democratTrack: mode === 'partial' ? mobileTrack(democrats.length, true, mobileCell) : [],
      republicanTrack: mode === 'partial' ? mobileTrack(republicans.length, false, mobileCell) : [],
      democratBrackets: mode === 'none' ? [] : mobileBrackets(democrats, mobileCell),
      republicanBrackets: mode === 'none' ? [] : mobileBrackets(republicans, mobileCell),
    },
    vertical: (() => {
      const size = verticalCellSize(options.width || 320);
      return {
        cell: size,
        columns: VERTICAL_COLUMNS,
        rows: Math.ceil(ordered.length / VERTICAL_COLUMNS),
        labelGutter: VERTICAL_LABEL_GUTTER,
        cells: builds('vertical') ? cellsFor(ordered, mode, showFlipped, verticalCoord) : [],
        brackets: verticalBrackets(ordered, mode, size),
        line: verticalLine(size),
        lineY: Math.floor(218 / VERTICAL_COLUMNS) * size,
      };
    })(),
    states,
    districts: seats.filter(seat => seat.targeted).sort((a, b) => (
      a.stateName.localeCompare(b.stateName) || a.district - b.district
    )),
  };
}
